import {
  queryOptions,
  type DefaultError,
  type QueryClient,
  useQueryClient,
} from '@tanstack/react-query';
import { ms } from 'ms';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { validate as isUUID } from 'uuid';

import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants/pagination';
import { EMPTY_DROPDOWN_OPTION } from '~/constants/select';

import {
  fetchSites,
  getSiteQueryOptions,
  getSitesBulkQueryOptions,
  queryKeysSite,
  type Site,
  type SitesQueryParams,
} from '~/data/site';
import { type SiteListItem } from '~/data/site/useQuerySites';
import { useGetUserUtils } from '~/data/user';

import { addressToString } from '@vestigas/utils';

import {
  type AsyncOption,
  OptionPrimarySecondary,
  type SelectDataSource,
  type SelectLoadRequest,
  useSelectDataSource,
} from '@vestigas/ui';

import { AsyncEntitySelect } from '../AsyncEntitySelect';

import { type Option } from './SelectServerDriven';

const getOptionLabel = ({ name }: { name: string }) => name;
const EMPTY_OPTION = { value: EMPTY_DROPDOWN_OPTION };
const EMPTY_SITE = {
  id: EMPTY_DROPDOWN_OPTION,
  name: EMPTY_DROPDOWN_OPTION,
} as Site | SiteListItem;
type SiteItem = Site | SiteListItem;
type FilteredSitesResponse = {
  data: SiteItem[];
  hasNextPage: boolean;
  nextOffset: number;
  paginatedCount: number;
  totalCount: number;
};
type SiteFilters = {
  readonly allowedSiteIds?: string[];
  readonly filterActive?: boolean;
};

function getSiteItemQueryOptions(siteId: string) {
  if (!isUUID(siteId)) {
    return undefined;
  }

  const { queryFn, queryKey } = getSiteQueryOptions<Site>({ siteId });
  return {
    meta: { suppressError: true },
    queryFn,
    queryKey,
    select: (site: unknown) => site as Site,
    staleTime: ms('60s'),
    throwOnError: false,
  };
}

function createEmptyFilteredSitesResponse(
  nextOffset: number,
): FilteredSitesResponse {
  return {
    data: [],
    hasNextPage: false,
    nextOffset,
    paginatedCount: 0,
    totalCount: 0,
  };
}

/**
 * Loads one unrestricted site page for the selector.
 *
 * @param queryParams - API pagination and search parameters.
 * @param signal - Abort signal for the request.
 * @returns The page with the next selector offset.
 */
async function fetchFilteredSitePage({
  queryParams,
  signal,
}: {
  queryParams: Partial<SitesQueryParams>;
  signal: AbortSignal;
}): Promise<FilteredSitesResponse> {
  const response = await fetchSites(queryParams, { signal });
  return {
    ...response,
    nextOffset: (queryParams.offset ?? 0) + PAGINATION_PAGE_SIZE_DEFAULT,
  };
}

/**
 * Builds the site selector list query for unrestricted or allow-listed sites.
 *
 * @param request - Search, pagination, and site-filter parameters from the selector.
 * @param queryClient - Client used to reuse cached bulk site responses.
 * @returns Query options producing selector-compatible site data.
 */
function getSiteListQueryOptions({
  filters,
  pageParam,
  queryClient,
  search,
}: SelectLoadRequest<number, SiteFilters> & {
  queryClient: QueryClient;
}) {
  const queryParams = {
    ...(typeof filters?.filterActive === 'boolean' && {
      filterActive: filters.filterActive,
    }),
    limit: PAGINATION_PAGE_SIZE_DEFAULT,
    offset: pageParam,
    searchString: search,
  };
  const allowedSiteIds = filters?.allowedSiteIds;

  return queryOptions({
    queryFn: async ({ signal }): Promise<FilteredSitesResponse> => {
      if (allowedSiteIds?.length === 0) {
        return createEmptyFilteredSitesResponse(queryParams.offset ?? 0);
      }

      if (allowedSiteIds) {
        const { items } = await queryClient.query(
          getSitesBulkQueryOptions({ siteIds: allowedSiteIds }),
        );
        const normalizedSearch = search.trim().toLocaleLowerCase();
        const data = items.filter((site) => {
          const matchesSearch =
            !normalizedSearch ||
            site.name.toLocaleLowerCase().includes(normalizedSearch);
          const matchesActive = filters?.filterActive !== true || site.isActive;

          return matchesSearch && matchesActive;
        });

        return {
          data,
          hasNextPage: false,
          nextOffset: 0,
          paginatedCount: data.length,
          totalCount: data.length,
        };
      }

      return fetchFilteredSitePage({ queryParams, signal });
    },
    queryKey: [
      ...queryKeysSite.getAll(queryParams),
      {
        allowedSiteIds,
        filterActive: filters?.filterActive,
        search: search.trim().toLocaleLowerCase(),
      },
    ] as const,
  });
}

function createSitePlaceholder(id: string): SiteItem {
  return { id, name: id } as SiteItem;
}

function getSelectedSiteData(
  id: string,
  data: SiteItem | readonly SiteItem[] | undefined,
) {
  if (id === EMPTY_DROPDOWN_OPTION) {
    return EMPTY_SITE;
  }

  return data && !Array.isArray(data)
    ? (data as SiteItem)
    : createSitePlaceholder(id);
}

function createFormatOptionLabel(
  formatter: ((option: Option<SiteItem>) => ReactNode) | undefined,
) {
  return formatter
    ? ({
        data,
        label,
        value,
      }: Extract<AsyncOption<SiteItem>, { status: 'resolved' }>) =>
        formatter({ data, label, value })
    : undefined;
}

function createRemovableValue(
  isRemovable: ((option: Option<SiteItem>) => boolean) | undefined,
) {
  return isRemovable
    ? (option: AsyncOption<SiteItem>) =>
        isRemovable({
          data:
            option.status === 'resolved'
              ? option.data
              : createSitePlaceholder(option.value),
          label: option.label,
          value: option.value,
        })
    : undefined;
}

/**
 * Formats site select options with address as secondary information.
 * ----------------------
 * <siteName>
 * <address (if available)>
 * ----------------------
 */
export const siteAddressOptionLabel = ({
  data,
  label,
}: Option<Site | SiteListItem>) => {
  // Format address if available, handling optional fields
  const formattedAddress = data.address
    ? addressToString(data.address)
    : undefined;

  return (
    <OptionPrimarySecondary
      primaryInformation={label}
      secondaryInformation={formattedAddress || undefined}
    />
  );
};

type P = {
  readonly 'data-testid'?: string;
  readonly filterActive?: boolean | null;
  readonly formatOptionLabel?: (
    option: Option<Site | SiteListItem>,
  ) => ReactNode;
  readonly hasEmptyOption?: boolean;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<Site | SiteListItem>) => boolean;
  /**
   * `undefined` allows all sites; an empty array allows none.
   */
  readonly allowedSiteIds?: string[];
  readonly onChange: {
    bivarianceHack(
      value: Array<{ id: string }> | { id: string },
      data: Array<Site | SiteListItem> | Site | SiteListItem,
    ): void;
  }['bivarianceHack'];
  readonly placeholder?: string;
  readonly value: string | string[];
} & ComponentStyling;

type SitesSelectProps = Pick<
  P,
  | 'className'
  | 'hasEmptyOption'
  | 'isClearable'
  | 'isDisabled'
  | 'isMultiSelect'
  | 'placeholder'
  | 'style'
> & {
  readonly dataSource: SelectDataSource<SiteItem, number, SiteFilters>;
  readonly filters: SiteFilters;
  readonly formatOptionLabel?: (
    option: Extract<AsyncOption<SiteItem>, { status: 'resolved' }>,
  ) => ReactNode;
  readonly hasReadPermission: boolean;
  readonly isRemovableValue?: (option: AsyncOption<SiteItem>) => boolean;
  readonly normalizedValues: string[];
  readonly noOptionsMessage?: () => string;
  readonly onChange: (
    value: string | null | string[],
    data: SiteItem | readonly SiteItem[] | undefined,
  ) => void;
  readonly testId?: string;
  readonly unresolvedLabel?: string;
};

function SitesSelect({
  className,
  dataSource,
  filters,
  formatOptionLabel,
  hasEmptyOption,
  hasReadPermission,
  isClearable,
  isDisabled,
  isMultiSelect,
  isRemovableValue,
  normalizedValues,
  noOptionsMessage,
  onChange,
  placeholder,
  style,
  testId,
  unresolvedLabel,
}: SitesSelectProps) {
  const sharedProps = {
    className,
    dataSource,
    emptyOption: hasEmptyOption ? EMPTY_OPTION : undefined,
    filters,
    formatOptionLabel,
    hasReadPermission,
    isClearable,
    isDisabled,
    isRemovableValue,
    noOptionsMessage,
    placeholder,
    testId,
    unresolvedLabel,
  };

  return (
    <div style={style}>
      {isMultiSelect ? (
        <AsyncEntitySelect<SiteItem, number, SiteFilters>
          {...sharedProps}
          isMulti
          value={normalizedValues}
          onChange={onChange}
        />
      ) : (
        <AsyncEntitySelect<SiteItem, number, SiteFilters>
          {...sharedProps}
          value={normalizedValues.find(Boolean) ?? null}
          onChange={onChange}
        />
      )}
    </div>
  );
}

export const MultiSelectSites = ({
  allowedSiteIds,
  className,
  'data-testid': testId,
  filterActive = true,
  formatOptionLabel,
  hasEmptyOption = false,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  onChange,
  placeholder = 'Standorte',
  style,
  value,
}: P) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { UserUtils } = useGetUserUtils();
  const filters = useMemo(
    (): SiteFilters => ({
      allowedSiteIds,
      filterActive:
        typeof filterActive === 'boolean' ? filterActive : undefined,
    }),
    [allowedSiteIds, filterActive],
  );
  const dataSource = useSelectDataSource<
    FilteredSitesResponse,
    SiteItem,
    number,
    DefaultError,
    SiteFilters
  >({
    getItems: ({ data }) => data,
    getNextPageParam: ({ hasNextPage, nextOffset }) =>
      hasNextPage ? nextOffset : undefined,
    getOptionLabel,
    getOptionValue: (site: SiteItem) => site.id,
    initialPageParam: 0,
    isPaginated: true,
    itemQueryOptions: getSiteItemQueryOptions,
    listQueryKey: [queryKeysSite.base(), 'list'],
    listQueryOptions: (request) =>
      getSiteListQueryOptions({ ...request, queryClient }),
  });
  const handleChange = useCallback(
    (
      nextValue: string | null | string[],
      nextData:
        | Site
        | SiteListItem
        | readonly (Site | SiteListItem)[]
        | undefined,
    ) => {
      if (Array.isArray(nextValue)) {
        const items: readonly (Site | SiteListItem)[] = Array.isArray(nextData)
          ? (nextData as readonly (Site | SiteListItem)[])
          : [];
        const dataById = new Map(items.map((item) => [item.id, item]));
        onChange(
          nextValue.map((id) => ({ id })),
          nextValue.map((id) =>
            id === EMPTY_DROPDOWN_OPTION
              ? EMPTY_SITE
              : (dataById.get(id) ?? createSitePlaceholder(id)),
          ),
        );
        return;
      }

      const nextId = nextValue ?? '';
      onChange({ id: nextId }, getSelectedSiteData(nextId, nextData));
    },
    [onChange],
  );
  const resolvedFormatOptionLabel = useMemo(
    () => createFormatOptionLabel(formatOptionLabel),
    [formatOptionLabel],
  );
  const removableValue = useMemo(
    () => createRemovableValue(isRemovableValue),
    [isRemovableValue],
  );
  const normalizedValues = useMemo(() => [value].flat(), [value]);

  return (
    <SitesSelect
      className={className}
      dataSource={dataSource}
      filters={filters}
      formatOptionLabel={resolvedFormatOptionLabel}
      hasEmptyOption={hasEmptyOption}
      hasReadPermission={UserUtils.isSiteReadAllowedUser()}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMultiSelect={isMultiSelect}
      isRemovableValue={removableValue}
      normalizedValues={normalizedValues}
      noOptionsMessage={
        allowedSiteIds?.length === 0
          ? () => t(($) => $.siteSelect.noAllowedSites)
          : undefined
      }
      placeholder={placeholder}
      unresolvedLabel={t(($) => $.siteSelect.unavailable)}
      style={style}
      testId={testId}
      onChange={handleChange}
    />
  );
};
